<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { picoSearch } from '@scmmishra/pico-search';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const audiences = useMapGetter('audiences/getAudiences');
const uiFlags = useMapGetter('audiences/getUIFlags');

const searchQuery = ref('');
const deleteDialogRef = ref(null);
const selectedAudience = ref(null);

const filteredAudiences = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return audiences.value;
  return picoSearch(audiences.value, query, [
    { name: 'name', weight: 4 },
    'description',
  ]);
});

const openCreatePage = () => {
  router.push(accountScopedRoute('audiences_new'));
};

const openEditPage = audience => {
  router.push(
    accountScopedRoute('audiences_edit', { audienceId: audience.id })
  );
};

const openDeleteDialog = audience => {
  selectedAudience.value = audience;
  deleteDialogRef.value?.open?.();
};

const deleteAudience = async () => {
  try {
    await store.dispatch('audiences/delete', selectedAudience.value.id);
    useAlert(t('AUDIENCE_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    deleteDialogRef.value?.close?.();
  } catch (error) {
    useAlert(
      error?.message || t('AUDIENCE_MGMT.DELETE.API.ERROR_MESSAGE')
    );
  }
};

onBeforeMount(() => {
  store.dispatch('audiences/get');
});
</script>

<template>
  <div
    class="flex flex-col flex-1 h-full m-0 overflow-auto bg-n-surface-1"
  >
    <div class="flex flex-col gap-4 p-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ $t('AUDIENCE_MGMT.HEADER') }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('AUDIENCE_MGMT.DESCRIPTION') }}
          </p>
        </div>
        <Button
          :label="$t('AUDIENCE_MGMT.HEADER_BTN_TXT')"
          size="sm"
          @click="openCreatePage"
        />
      </div>

      <div class="max-w-xs">
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="$t('AUDIENCE_MGMT.SEARCH_PLACEHOLDER')"
          class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
        />
      </div>
    </div>

    <div v-if="uiFlags.isFetching" class="flex items-center justify-center py-10">
      <Spinner />
    </div>

    <div v-else-if="!filteredAudiences.length" class="flex items-center justify-center py-10">
      <span class="text-sm text-n-slate-11">
        {{ searchQuery ? $t('AUDIENCE_MGMT.NO_RESULTS') : $t('AUDIENCE_MGMT.LIST.404') }}
      </span>
    </div>

    <div v-else class="px-6 pb-6">
      <div class="grid gap-4">
        <div
          v-for="audience in filteredAudiences"
          :key="audience.id"
          class="flex items-center justify-between p-4 border rounded-xl border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors"
        >
          <div class="flex flex-col gap-1 flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-n-slate-12 truncate">
                {{ audience.name }}
              </span>
              <span
                class="px-2 py-0.5 text-xs font-medium rounded-full"
                :class="audience.status === 'active'
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-slate-3 text-n-slate-11'"
              >
                {{ audience.status === 'active'
                  ? $t('AUDIENCE_MGMT.STATUS.ACTIVE')
                  : $t('AUDIENCE_MGMT.STATUS.INACTIVE') }}
              </span>
            </div>
            <span v-if="audience.description" class="text-xs text-n-slate-11 truncate">
              {{ audience.description }}
            </span>
            <span class="text-xs text-n-slate-9">
              {{ $t('AUDIENCE_MGMT.LIST.FILTERS_COUNT', { count: audience.filters?.length || 0 }) }}
            </span>
          </div>
          <div class="flex gap-2 flex-shrink-0 ml-4">
            <Button
              v-tooltip.top="$t('AUDIENCE_MGMT.FORM.EDIT')"
              icon="i-woot-edit-pen"
              slate
              sm
              @click="openEditPage(audience)"
            />
            <Button
              v-tooltip.top="$t('AUDIENCE_MGMT.FORM.DELETE')"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="openDeleteDialog(audience)"
            />
          </div>
        </div>
      </div>
    </div>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="$t('AUDIENCE_MGMT.DELETE.CONFIRM.TITLE')"
      :description="$t('AUDIENCE_MGMT.DELETE.CONFIRM.MESSAGE', { name: selectedAudience?.name })"
      :confirm-button-label="$t('AUDIENCE_MGMT.DELETE.CONFIRM.YES')"
      @confirm="deleteAudience"
    />
  </div>
</template>
